import { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ProcessReferenceContent } from '../components/ProcessReferenceContent';
import { PROCESS_REFERENCE, findProcessReference } from '../lib/process-reference';

export function ProcessesPage(): ReactElement {
  const [params] = useSearchParams();
  const selected = findProcessReference(params.get('flujo')) ?? PROCESS_REFERENCE[0];
  const menuRef = useRef<HTMLElement>(null);

  // En un telefono los flujos son una tira que se desliza de lado (global.css):
  // el elegido se trae al centro moviendo solo la tira, no la pagina. Llegar con
  // `?flujo=F12` lo muestra sin buscarlo. En escritorio la lista no desborda de
  // lado y esto no hace nada.
  useEffect(() => {
    const menu = menuRef.current;
    const current = menu?.querySelector<HTMLElement>('[aria-current="page"]') ?? null;
    if (menu === null || current === null || menu.scrollWidth <= menu.clientWidth) {
      return;
    }
    const offset = current.getBoundingClientRect().left - menu.getBoundingClientRect().left;
    menu.scrollLeft += offset - (menu.clientWidth - current.offsetWidth) / 2;
  }, [selected.id]);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Procesos</h1>
          <p className="page__subtitle">
            Referencia de los {PROCESS_REFERENCE.length} flujos del middleware: qué los inicia, cómo
            recorren los sistemas y qué producen.
          </p>
        </div>
        <span className="process-reference-tag">Guía de referencia</span>
      </header>
      <div className="process-layout">
        <nav ref={menuRef} className="process-menu" aria-label="Flujos de referencia">
          {PROCESS_REFERENCE.map((process) => (
            <Link
              key={process.id}
              to={`/procesos?flujo=${process.id}`}
              className="process-menu__item"
              aria-current={selected.id === process.id ? 'page' : undefined}
            >
              <span className="process-menu__id">{process.id}</span>
              <span> · {process.title}</span>
            </Link>
          ))}
        </nav>
        <article className="process-detail" aria-labelledby="process-title">
          <ProcessReferenceContent process={selected} titleId="process-title" />
          <section className="panel">
            <header className="panel__header">
              <h3>Cómo se supervisa</h3>
            </header>
            <div className="panel__body">
              <p>
                <Link to={`/corridas?flujo=${selected.id}`}>Ver las corridas de {selected.id}</Link>
                : cada ejecución del flujo con su estado, y desde ella su grafo y las corridas
                anteriores.
              </p>
              <p>
                El middleware valida los datos y registra los mensajes antes de procesarlos. Las
                referencias entre sistemas permiten seguir cada operación y evitar duplicados.
              </p>
              <p>
                Los datos inválidos se revisan en <Link to="/cuarentena">Cuarentena</Link>. Los
                errores que agotan sus reintentos se consultan en{' '}
                <Link to="/dead-letters">Dead letters</Link>. Desde un mensaje puedes abrir la traza
                y el grafo de su corrida.
              </p>
              <p className="muted">
                Esta guía describe el proceso y sus dependencias; la actividad real se consulta en
                los mensajes y el tablero. Referencia: plan del proyecto, documentación de cada
                flujo e implementación del middleware.
              </p>
            </div>
          </section>
        </article>
      </div>
    </div>
  );
}
